import type { Nullable } from "../../pojo/Util";

/**
 * A utility type for isUndefined that will limit the scope of T to any of
 * null / undefined / number that T contains. e.g.:
 * <any> returns null | undefined | number
 * <string | Date | number | null> returns null | number
 * <string> returns never
 *
 * unfortunately, due to TS limitations, we can't narrow the type
 * from number to NaN specifically
 */
type ExtractNaNOrNullableFrom<T> = null | undefined | number extends T
    ? null | undefined | number
    : Extract<T, null | undefined | number>;

export function isUndefined<T = any>(obj: T): obj is ExtractNaNOrNullableFrom<T> {
    return typeof obj === "undefined" || obj === null || Number.isNaN(obj);
}

export function isDefined<T = any>(obj: T): obj is NonNullable<T> {
    return !isUndefined(obj);
}

export function isDefinedStrict<T>(val: Nullable<T>): val is T {
    return val !== undefined && val !== null;
}

export function isObject(value: unknown): value is Record<string, any> {
    return (!!value && value.constructor && value.constructor === Object) || (!!value && typeof value === "object");
}

export function isFunction(fn: any): boolean {
    return typeof fn === "function";
}

export function isString(obj: unknown): obj is string {
    return !!obj && typeof obj === "string";
}

export function isDate(date: unknown): date is Date {
    return date instanceof Date;
}

export function isArray(obj: unknown): obj is any[] {
    return !isUndefined(obj) && obj.constructor === Array;
}
